import { TopicDto } from "@growth/dto";
import { Badge, Button, Field, Input, Textarea } from "@growth/ui";
import { ArrowLeft, ExternalLink, LoaderCircle, Sparkles, WandSparkles } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { NoBlog } from "../components/no-blog";
import { useActive } from "../lib/active";
import { useAiStatus, useRuns, useStartRun, useSuggestTopics } from "../lib/ai";
import { apiErrorMessage } from "../lib/api-error";
import { formatRelative } from "../lib/format";

export const AiStudioPage = () => {
  const { brand } = useActive();
  if (brand && !brand.siteId) return <NoBlog />;
  return <AiStudio brandName={brand?.name ?? ""} />;
};

const AiStudio = ({ brandName }: { brandName: string }) => {
  const navigate = useNavigate();
  const status = useAiStatus();
  const topics = useSuggestTopics();
  const start = useStartRun();
  const runs = useRuns();
  const [form, setForm] = useState({ title: "", description: "", notes: "" });

  const applyTopic = (topic: TopicDto) => {
    setForm({
      title: topic.title,
      description: `${topic.angle}\n\nWhy now: ${topic.whyNow}`,
      notes: topic.sources.length
        ? `Starting sources:\n${topic.sources.map((s) => `- ${s.title}: ${s.url}`).join("\n")}`
        : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    start.mutate(
      { title: form.title, description: form.description, notes: form.notes || undefined },
      { onSuccess: ({ id }) => navigate(`/ai/runs/${id}`) },
    );
  };

  const disabled = status.data && !status.data.enabled;

  return (
    <section className="mx-auto max-w-4xl space-y-8">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink">
        <ArrowLeft className="size-4" />
        {brandName} posts
      </Link>
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-muted">{brandName}</p>
        <h1 className="font-serif text-3xl text-ink">Write with AI</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted">
          Give a title and what it should cover, or let the scout suggest timely topics. The agents
          research it, write and critique a draft, add cited charts and images where they help, and
          create a cover. The result is a draft for you to review — nothing is published.
        </p>
      </header>

      {disabled && (
        <p
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          AI is switched off: add <code>OPENAI_API_KEY</code> to the server environment and restart
          the server.
        </p>
      )}

      <form onSubmit={submit} className="space-y-4 rounded-xl border border-line bg-white p-6">
        <Field label="Title or topic">
          {(props) => (
            <Input
              {...props}
              required
              minLength={3}
              maxLength={200}
              value={form.title}
              placeholder="e.g. How to answer 'tell me about yourself' in a grad interview"
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          )}
        </Field>
        <Field
          label="What it should cover"
          hint="The angle, the reader, and anything it must include."
        >
          {(props) => (
            <Textarea
              {...props}
              required
              minLength={10}
              maxLength={2000}
              rows={4}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          )}
        </Field>
        <Field
          label="Notes for the writer (optional)"
          hint="Sources to use, examples to include, things to avoid."
        >
          {(props) => (
            <Textarea
              {...props}
              maxLength={2000}
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          )}
        </Field>
        {start.isError && (
          <p role="alert" className="text-sm text-red-600">
            {apiErrorMessage(start.error, "Couldn't start the run")}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button
            type="button"
            variant="secondary"
            disabled={disabled || topics.isPending}
            onClick={() => topics.mutate()}
          >
            {topics.isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <Sparkles className="size-4" />
            )}
            {topics.isPending ? "Scouting topics… (about a minute)" : "Suggest timely topics"}
          </Button>
          <Button type="submit" disabled={disabled || start.isPending}>
            {start.isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <WandSparkles className="size-4" />
            )}
            Write the draft
          </Button>
        </div>
      </form>

      {topics.isError && (
        <p role="alert" className="text-sm text-red-600">
          {apiErrorMessage(topics.error, "Couldn't suggest topics")}
        </p>
      )}
      {topics.data && (
        <section className="space-y-3" aria-label="Suggested topics">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
            Suggested topics · about US${topics.data.costUsd.toFixed(2)}
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            {topics.data.topics.map((topic) => (
              <article
                key={topic.title}
                className="flex flex-col gap-2 rounded-xl border border-line bg-white p-4"
              >
                <h3 className="font-serif text-lg leading-snug text-ink">{topic.title}</h3>
                <p className="text-sm text-ink/80">{topic.angle}</p>
                <p className="text-xs text-muted">
                  <span className="font-medium text-ink">Why now:</span> {topic.whyNow}
                </p>
                {topic.sources.length > 0 && (
                  <ul className="space-y-0.5 text-xs">
                    {topic.sources.slice(0, 3).map((source) => (
                      <li key={source.url}>
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-accent hover:underline"
                        >
                          {source.title}
                          <ExternalLink className="size-3" />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                <Button
                  variant="secondary"
                  className="mt-auto self-start"
                  onClick={() => applyTopic(topic)}
                >
                  Use this topic
                </Button>
              </article>
            ))}
          </div>
        </section>
      )}

      {runs.data && runs.data.length > 0 && (
        <section className="space-y-3" aria-label="Recent runs">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
            Recent runs
          </h2>
          <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-white">
            {runs.data.map((run) => (
              <li key={run.id}>
                <Link
                  to={`/ai/runs/${run.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-paper/40"
                >
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{run.title}</span>
                  {run.status === "RUNNING" ? (
                    <Badge tone="accent">
                      <LoaderCircle className="size-3 animate-spin" /> {run.stage}
                    </Badge>
                  ) : run.status === "SUCCEEDED" ? (
                    <Badge tone="success">Draft ready</Badge>
                  ) : (
                    <Badge>Failed</Badge>
                  )}
                  <span className="w-20 text-right text-xs text-muted">
                    {formatRelative(run.createdAt)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
};
